import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    bottom: 1rem;
    right: 1rem;
    left: 1rem;
  }
`;

const ToastItem = styled.div`
  background: ${props => {
    switch (props.$type) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      default: return '#3b82f6';
    }
  }};
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 280px;
  max-width: 400px;
  animation: ${slideIn} 0.3s ease, ${slideOut} 0.3s ease ${props => props.$duration || 3}s forwards;
  cursor: pointer;
  
  @media (max-width: 768px) {
    width: calc(100vw - 2rem);
    min-width: auto;
  }
`;

const ToastIcon = styled.span`
  font-size: 1.2rem;
`;

const ToastMessage = styled.span`
  flex: 1;
  font-size: 0.9rem;
  font-weight: 500;
`;

class ToastManagerClass {
  constructor() {
    this.toasts = [];
    this.listeners = new Set();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify() {
    this.listeners.forEach(listener => listener([...this.toasts]));
  }

  add(message, type = 'info', duration = 3000) {
    const id = Date.now() + Math.random();
    this.toasts.push({ id, message, type, duration });
    this.notify();
    
    setTimeout(() => {
      this.remove(id);
    }, duration);
    
    return id;
  }

  remove(id) {
    this.toasts = this.toasts.filter(t => t.id !== id);
    this.notify();
  }

  success(message, duration = 3000) {
    return this.add(message, 'success', duration);
  }

  error(message, duration = 4000) {
    return this.add(message, 'error', duration);
  }

  warning(message, duration = 3000) {
    return this.add(message, 'warning', duration);
  }

  info(message, duration = 3000) {
    return this.add(message, 'info', duration);
  }
}

export const toast = new ToastManagerClass();

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = React.useState([]);

  React.useEffect(() => {
    return toast.subscribe(setToasts);
  }, []);

  return (
    <>
      {children}
      <ToastContainer>
        {toasts.map(t => (
          <ToastItem
            key={t.id}
            $type={t.type}
            $duration={t.duration / 1000}
            onClick={() => toast.remove(t.id)}
          >
            <ToastIcon>
              {t.type === 'success' && '✅'}
              {t.type === 'error' && '❌'}
              {t.type === 'warning' && '⚠️'}
              {t.type === 'info' && 'ℹ️'}
            </ToastIcon>
            <ToastMessage>{t.message}</ToastMessage>
          </ToastItem>
        ))}
      </ToastContainer>
    </>
  );
};